/**
 * 開発者ワンクリックログイン（ARCHITECTURE.md §7.11 / 05-ui.md §10）。
 *
 * 描画条件は **`VITE_DEV_LOGIN === '1'` と サーバ真実 `publicConfig.devLogin` の AND**。
 * どちらか一方でも欠けたら描画しない。認証情報はクライアントに一切持たず、
 * サーバー側が env の seed 済みデモアカウントでセッションを発行する。
 *
 * 多層防御のうち UI が担うのはここまでで、最終的な可否はサーバーが決める
 * （本番の API は `/dev-login` を 404 にする）。
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, Button } from '@mail-sv/ui';
import { FlaskConical } from 'lucide-react';
import { devLogin, fetchPublicConfig } from '../lib/auth.js';
import { qk } from '../lib/query-keys.js';
import { env } from '../env.js';
import { errorMessage } from '../lib/api.js';

export function DevLoginButton({ onSuccess }: { onSuccess: () => void }) {
  // 層 1: ビルド時フラグ。本番ビルドではここで消えるので、以降のコードは
  //       バンドルにすら乗らない（フックより前に return して問題ない値）。
  const buildFlag = env.devLogin;

  // 層 2: サーバ真実。dev でない API に対しては false が返る。
  const config = useQuery({
    queryKey: qk.publicConfig(),
    queryFn: fetchPublicConfig,
    enabled: buildFlag,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const mut = useMutation({
    mutationFn: () => devLogin(),
    onSuccess,
  });

  if (!buildFlag || config.data?.devLogin !== true) return null;

  return (
    <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-3">
      <div className="flex items-center gap-2 text-[13px] font-medium text-warning">
        <FlaskConical className="size-4" aria-hidden />
        開発モード — この表示は本番環境には出ません
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full border-warning/50"
        onClick={() => mut.mutate()}
        loading={mut.isPending}
      >
        <FlaskConical aria-hidden />
        {import.meta.env.VITE_DEMO_LOGIN === '1' ? 'デモとしてログイン' : '開発者としてログイン'}
      </Button>
      {mut.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(mut.error)}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

/** ログイン後もヘッダに出し続ける「開発環境」バッジ。 */
export function DevEnvBadge() {
  if (!env.devLogin) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">
      <FlaskConical className="size-3" aria-hidden />
      開発環境
    </span>
  );
}
